import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  useMapTemplates,
  usePurchasedEduMaps,
  usePurchasedMaps,
} from '@zep/apis';
import { useAuth } from '@zep/auth';
import { MapTemplate } from '@zep/types';

import { resolveMapTemplateLocale } from './templateLocale';

interface UseTemplatesOptions {
  categoryId?: number;
  isPurchasedView?: boolean;
  suspense?: boolean;
}

export const useTemplates = (options: UseTemplatesOptions = {}) => {
  const { categoryId, isPurchasedView = false, suspense = true } = options;
  const { user } = useAuth();
  const { i18n } = useTranslation();
  const isEdu = user?.isEdu ?? false;
  const templateLocale = resolveMapTemplateLocale(i18n.resolvedLanguage);

  const queryOptions = {
    locale: templateLocale,
  };
  const paginationOptions = {
    page: 1,
    limit: 100,
  };

  const { data: mapTemplates } = useMapTemplates(
    {
      locale: templateLocale,
      category: categoryId,
    },
    paginationOptions,
    {
      suspense,
      enabled: !isPurchasedView,
    },
  );

  const purchasedMapsQuery = usePurchasedMaps(queryOptions, paginationOptions, {
    enabled: isPurchasedView && !isEdu,
  });

  const purchasedEduMapsQuery = usePurchasedEduMaps(
    queryOptions,
    paginationOptions,
    {
      enabled: isPurchasedView && isEdu,
    },
  );

  const purchasedData = isEdu
    ? purchasedEduMapsQuery.data
    : purchasedMapsQuery.data;

  const templates = useMemo<MapTemplate[] | undefined>(() => {
    if (isPurchasedView) {
      return purchasedData?.pages
        .map(d =>
          d.items.map(asset => ({
            hashId: asset.hashId,
            name: asset.name,
            thumbnailUrl: asset.finalImageUrl,
            createdAt: asset.createdAt,
          })),
        )
        .flat();
    } else {
      return mapTemplates?.pages.flatMap(d => d.results);
    }
  }, [purchasedData, mapTemplates, isPurchasedView]);

  return {
    templates,
  };
};
